(function attachOmbreCollectionLoadMore(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.OmbreCollectionLoadMore = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function createOmbreCollectionLoadMore() {
  const defaultParseDocument = (html) => new DOMParser().parseFromString(html, 'text/html');

  const setLoadingState = (button, status) => {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Loading…';
    if (status) status.textContent = 'Loading more products…';
  };

  const setReadyState = (button, status) => {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = 'Load More';
    if (status) status.textContent = '';
  };

  const setErrorState = (button, status) => {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = 'Try Again';
    if (status) status.textContent = 'More products could not be loaded. Please try again.';
  };

  const pendingLoads = new WeakMap();
  function loadNextCollectionPage(options) {
    const button = options.button;
    if (button && pendingLoads.has(button)) return pendingLoads.get(button);
    const pending = loadPage(options).finally(() => {
      if (button) pendingLoads.delete(button);
    });
    if (button) pendingLoads.set(button, pending);
    return pending;
  }

  async function loadPage({
    section,
    grid,
    button,
    fetchPage = (...args) => fetch(...args),
    parseDocument = defaultParseDocument,
    onProductsAdded = () => {}
  }) {
    if (!section || !grid || !button || button.disabled) {
      return { appendedCount: 0, hasNextPage: Boolean(button?.dataset?.nextUrl) };
    }

    const nextUrl = button.dataset.nextUrl;
    const container = button.closest('[data-load-more-container]');
    const status = container?.querySelector('[data-load-more-status]');
    if (!nextUrl) {
      container?.remove();
      return { appendedCount: 0, hasNextPage: false };
    }

    setLoadingState(button, status);

    try {
      let requestUrl = nextUrl;
      const visitedUrls = new Set();

      while (requestUrl) {
        if (visitedUrls.has(requestUrl)) throw new Error('Unable to load more products');
        visitedUrls.add(requestUrl);

        const response = await fetchPage(requestUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('Unable to load more products');
        if (response.url) visitedUrls.add(response.url);

        const parsedDocument = parseDocument(await response.text());
        const sectionId = section.dataset.sectionId || '';
        const nextSection = parsedDocument.querySelector(
          `[data-shop-all-demo-section][data-section-id="${sectionId}"]`
        );
        const nextGrid = nextSection?.querySelector('[data-product-grid]');
        const newCards = nextGrid ? Array.from(nextGrid.querySelectorAll('[data-product-card]')) : [];
        if (!nextSection) throw new Error('Unable to load more products');

        const nextButton = nextSection.querySelector('[data-load-more]');
        const followingUrl = nextButton?.dataset.nextUrl;
        const followingBaseUrl = response.url
          || (typeof window !== 'undefined' ? window.location.href : requestUrl);
        const resolvedFollowingUrl = followingUrl
          ? new URL(followingUrl, followingBaseUrl).href
          : '';

        if (newCards.length === 0) {
          if (resolvedFollowingUrl) {
            requestUrl = resolvedFollowingUrl;
            continue;
          }

          setReadyState(button, status);
          container?.remove();
          return { appendedCount: 0, hasNextPage: false };
        }

        const existingCards = grid.querySelectorAll
          ? Array.from(grid.querySelectorAll('[data-product-card]'))
          : [];
        let nextIndex = existingCards.reduce(
          (highest, card) => Math.max(highest, Number(card.dataset.index || 0)),
          0
        );
        newCards.forEach((card) => {
          nextIndex += 1;
          card.dataset.index = String(nextIndex);
          grid.appendChild(card);
        });
        await onProductsAdded(newCards);

        if (resolvedFollowingUrl) {
          button.dataset.nextUrl = resolvedFollowingUrl;
          setReadyState(button, status);
          return { appendedCount: newCards.length, hasNextPage: true };
        }

        container?.remove();
        return { appendedCount: newCards.length, hasNextPage: false };
      }
    } catch (error) {
      setErrorState(button, status);
      throw new Error('Unable to load more products', { cause: error });
    }
  }

  return { loadNextCollectionPage };
});
